import { centsToInputValue, inputValueToCents } from '../../lib/money';

// Shared currency-input components for the rollout master grid + row editor
// Budget surfaces (Pre-P2b-3 polish, Fix 4). Both surfaces author the same
// fields with the same wire shape; the only differences are visual width
// and the surface-local drift-handling for Monthly. Drift logic stays at
// each call site — these components only own the input widget + the
// "commit on blur, only when the value actually changed" semantics.
//
// Two flavours:
//   - <MonthlyBudgetInput>  — Meta / Google master monthly. Caller decides
//     what to do with the new value (drift modal, direct mutate, etc.) via
//     the onCommit callback. Returns null when the user clears the input.
//   - <IncreaseInput>       — Campaign Increase. Always direct-write; never
//     triggers drift (per-rollout field). The component owns the no-op
//     guard: it only fires onCommit when the value actually changed and is
//     a valid integer-cents number.

type CommonProps = {
  // Initial value in cents. The input is uncontrolled — defaultValue is
  // derived from this on each render. To prevent stale values bleeding
  // across rerenders the caller should remount the component when the row
  // changes (the row id makes a fine `key`).
  valueCents: number | null;
  disabled?: boolean;
  // Tailwind width class so each surface controls its own sizing — the
  // master grid uses w-24 / w-20 (compact), the row editor uses a wider
  // input. Defaults to the master-grid sizing.
  widthClass?: string;
  ariaLabel?: string;
};

export type MonthlyBudgetInputProps = CommonProps & {
  // Fired on blur, ONLY when the new value differs from valueCents and
  // parses as a valid non-negative integer. Caller decides whether to
  // open the drift modal, fire patchRowBudget directly, etc.
  onCommit: (cents: number | null) => void;
};

export function MonthlyBudgetInput({
  valueCents,
  disabled,
  widthClass = 'w-24',
  ariaLabel,
  onCommit,
}: MonthlyBudgetInputProps) {
  return (
    <input
      type="text"
      defaultValue={centsToInputValue(valueCents)}
      disabled={disabled}
      aria-label={ariaLabel}
      onBlur={(e) => {
        const raw = e.target.value;
        const next = raw.trim() === '' ? null : inputValueToCents(raw);
        if (next === undefined) return; // unparseable, let user fix
        if (next === valueCents) return; // no-op
        onCommit(next);
      }}
      className={`${widthClass} rounded border border-slate-200 px-1 py-0.5 text-xs disabled:opacity-60 disabled:cursor-not-allowed`}
    />
  );
}

export type IncreaseInputProps = CommonProps & {
  // Fired on blur, ONLY when the new value differs from valueCents and
  // parses as a valid non-negative integer. Caller direct-writes via
  // patchRow({ metaCampaignIncrease } / { googleCampaignIncrease }).
  onCommit: (cents: number) => void;
};

export function IncreaseInput({
  valueCents,
  disabled,
  widthClass = 'w-20',
  ariaLabel,
  onCommit,
}: IncreaseInputProps) {
  // Increase is always a number (default 0) — never null. We treat empty
  // input as "user cleared, leave at 0" but only fire onCommit if the
  // resolved value differs from the stored one.
  const stored = valueCents ?? 0;
  return (
    <input
      type="text"
      defaultValue={centsToInputValue(stored)}
      disabled={disabled}
      aria-label={ariaLabel}
      onBlur={(e) => {
        const parsed = inputValueToCents(e.target.value);
        const next = parsed ?? 0;
        if (next === stored) return;
        onCommit(next);
      }}
      className={`${widthClass} rounded border border-slate-200 px-1 py-0.5 text-xs disabled:opacity-60 disabled:cursor-not-allowed`}
    />
  );
}
